var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import styled from 'styled-components/macro';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import * as Sentry from '@sentry/react';
import LineItemCalculator from '@nomady/shared/controllers/LineItemCalculator';
import { pathByRouteName } from '@nomady/shared/utils/routes';
import Page from '@nomady/shared/routeConfiguration/Page';
import { PaymentMode } from '@nomady/shared/config';
import getListingCheckInTime from '@nomady/shared/utils/listing';
import { getDeeplLocale, } from '@nomady/shared/marketplace-custom-config';
import { getPaymentMethods } from '@nomady/shared/utils/getPaymentMethods';
import { getCaptureMethod } from '@nomady/shared/utils/getCaptureMethod';
import config from '../../config';
import { clearData as clearSessionStorageData, retrieveData, } from './utils/sessionStorage';
import { clearData as clearLocalStorageData } from '../ListingPage/utils/localStorage';
import { initiateOrder, setTransaction, resetCheckout, requestPaymentTransitionTimeSelector, recordAnalytics, setInitialValues, confirmPayment, paymentModeSelector, } from './CheckoutPageSlice';
import getOrderParams from './utils/getOrderParams';
import { routeConfiguration } from '../..';
import CheckoutPageContainer from './partials/CheckoutPageContainer';
import Layout, { LayoutRow, LayoutRowHorizontalVariant, } from '../../styled-components/Layout/Layout';
import ListingPreviewCard from './partials/ListingPreviewCard';
import getLocationInformation from '../../util/addressHelpers';
import { currentUserSelector } from '../../ducks/user.duck';
import { STORAGE_KEY_BOOKING_AUTH_FLOW, resetListingPage, } from '../ListingPage/ListingPageSlice';
import BookingDetailsSection from './partials/sections/BookingDetailsSection';
import CheckoutSection from './partials/sections/CheckoutSection';
import MobileView from '../../styled-components/MobileView/MobileView';
import CheckoutInfo from './partials/CheckoutInfo';
import PaymentSection from './partials/sections/PaymentSection';
import { DefaultTheme } from '../../theme';
import MessageSection from './partials/sections/MessageSection';
import getListingUrl from './utils/getListingUrl';
import { localeSelector } from '../../slices/UISlice';
import ExpiredPaymentModal from './partials/ExpiredPaymentModal';
import FullPageSheet from '../../styled-components/FullPageSheet/FullPageSheet';
import { Information } from '../../styled-components/SectionGiftCardOrderConfirmation/SectionGiftCardOrderConfirmation';
import Text, { TextVariant } from '../../styled-components/Text/Text';
import LoadingAnimation from '../../components/LoadingAnimation/LoadingAnimation';
import { isApp } from '../../util/deviceContext';
import { CUSTOMER_COMMISSION_DISCOUNT_CODE_STORAGE_KEY } from '../ListingPage/ListingPage';
export const STORAGE_KEY = 'CheckoutPage';
const stripePromise = loadStripe(config.stripe.publishableKey);
const CheckoutLayout = styled.div `
  margin-top: 6px;
  @media ${props => props.theme.device.desktop} {
    display: grid;
    grid-template-columns: 2fr 1fr;
    position: relative;
    margin-top: 0;
  }
`;
const SidePanelContainer = styled.div `
  display: none;
  @media ${props => props.theme.device.desktop} {
    display: initial;
    height: 100%;
    padding: 0px 0px 10px 50px;
  }
`;
const SidePanel = styled.div `
  position: sticky;
  width: 100%;
  top: 80px;
  z-index: ${props => props.theme.priority.always};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const SpacedMobileView = styled(MobileView) `
  margin-bottom: 30px;
`;
const CheckoutPage = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    const dispatch = useDispatch();
    const pageState = useSelector((state) => state.CheckoutPage);
    const currentUser = useSelector(currentUserSelector);
    const requestPaymentTimestamp = useSelector(requestPaymentTransitionTimeSelector);
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const locale = useSelector(localeSelector);
    const paymentMode = useSelector(paymentModeSelector);
    const stripeFormRef = useRef(null);
    const messageFormRef = useRef(null);
    const [confirmPaymentInProgress, setConfirmPaymentInProgress] = useState(false);
    const [checkoutError, setCheckoutError] = useState();
    const [paymentInProgress, setPaymentInProgress] = useState();
    const [showExpiredTransactionModal, setShowExpiredTransactionModal] = useState(false);
    const { listing, giftCards, customerCommissionDiscountCode, providerStripeAccountId, giftCardInputNotEmpty, } = pageState;
    const instantBooking = !!((_b = (_a = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _a === void 0 ? void 0 : _a.publicData) === null || _b === void 0 ? void 0 : _b.instantBookingAvailable);
    const { seatQuantity = 1, adultQuantity = 1, teenQuantity = 0, childQuantity = 0, infantQuantity = 0, dogQuantity = 0, extras = {}, } = pageState.bookingData || {};
    const { bookingStart, bookingEnd } = pageState.bookingDates || {};
    const requiredDataAvailable = !!(listing &&
        bookingStart &&
        bookingEnd &&
        currentUser &&
        providerStripeAccountId);
    const onPaymentSuccessful = (transaction) => {
        var _a, _b;
        setPaymentInProgress(false);
        if (listing) {
            dispatch(recordAnalytics(transaction, listing, { bookingStart, bookingEnd }));
        }
        const transactionId = (_a = transaction === null || transaction === void 0 ? void 0 : transaction.id) === null || _a === void 0 ? void 0 : _a.uuid;
        const orderDetailsPath = pathByRouteName(Page.OrderDetails, routeConfiguration, {
            id: transactionId,
        }, {
            booked: true,
        });
        clearSessionStorageData(STORAGE_KEY);
        clearLocalStorageData(STORAGE_KEY_BOOKING_AUTH_FLOW);
        clearSessionStorageData(CUSTOMER_COMMISSION_DISCOUNT_CODE_STORAGE_KEY);
        (_b = messageFormRef === null || messageFormRef === void 0 ? void 0 : messageFormRef.current) === null || _b === void 0 ? void 0 : _b.requestSubmit();
        dispatch(resetListingPage());
        dispatch(resetCheckout());
        setPaymentInProgress(false);
        history.push(orderDetailsPath);
    };
    const handleCheckoutError = (error) => {
        if (error) {
            Sentry.captureException(error);
        }
        setPaymentInProgress(false);
        setCheckoutError(error);
    };
    useEffect(() => {
        var _a, _b;
        if (!requiredDataAvailable) {
            const storedData = retrieveData(STORAGE_KEY);
            const sessionStorageDataAvailable = (storedData === null || storedData === void 0 ? void 0 : storedData.listing) &&
                ((_a = storedData === null || storedData === void 0 ? void 0 : storedData.bookingDates) === null || _a === void 0 ? void 0 : _a.bookingStart) &&
                ((_b = storedData === null || storedData === void 0 ? void 0 : storedData.bookingDates) === null || _b === void 0 ? void 0 : _b.bookingEnd) &&
                (storedData === null || storedData === void 0 ? void 0 : storedData.providerStripeAccountId);
            if (sessionStorageDataAvailable) {
                dispatch(setInitialValues(storedData));
            }
            else {
                const listingUrl = getListingUrl(location.pathname);
                history.push(listingUrl);
            }
        }
        const handleStripeReturn = () => __awaiter(void 0, void 0, void 0, function* () {
            var _c;
            const storedData = retrieveData(STORAGE_KEY);
            if (storedData.transaction) {
                setConfirmPaymentInProgress(true);
                let status = 'payment_intent_processing';
                while (status === 'payment_intent_processing') {
                    const response = (yield dispatch(confirmPayment(storedData.transaction.id, !!((_c = storedData.listing) === null || _c === void 0 ? void 0 : _c.attributes.publicData.instantBookingAvailable))));
                    if (response === 'success') {
                        onPaymentSuccessful(storedData.transaction);
                    }
                    else if (response === 'payment_intent_requires_payment_method') {
                        handleCheckoutError({
                            message: intl.formatMessage({
                                id: `CheckoutPage.stripeError.redirectStatusFailed`,
                            }),
                            code: 'stripe-failed-return-state',
                        });
                    }
                    status = response;
                    yield new Promise(r => setTimeout(r, 5000));
                }
                setConfirmPaymentInProgress(false);
            }
        });
        handleStripeReturn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);
    useEffect(() => {
        var _a;
        Sentry.setContext('bookingData', {
            paymentMode,
            listingId: (_a = listing === null || listing === void 0 ? void 0 : listing.id) === null || _a === void 0 ? void 0 : _a.uuid,
            seats: seatQuantity,
            adults: adultQuantity,
            teens: teenQuantity,
            children: childQuantity,
            infants: infantQuantity,
            dogs: dogQuantity,
            giftCards,
            customerCommissionDiscountCode,
            extras,
            bookingStart,
            bookingEnd,
        });
    }, [
        adultQuantity,
        bookingEnd,
        bookingStart,
        childQuantity,
        customerCommissionDiscountCode,
        dogQuantity,
        extras,
        giftCards,
        infantQuantity,
        listing,
        paymentMode,
        seatQuantity,
        teenQuantity,
    ]);
    // Consider designing a loading state of the page in case this takes long.
    if (!requiredDataAvailable) {
        return _jsx(_Fragment, {});
    }
    const lineItemCalculator = new LineItemCalculator(listing, {
        seats: seatQuantity,
        adults: adultQuantity,
        teens: teenQuantity,
        children: childQuantity,
        infants: infantQuantity,
        dogs: dogQuantity,
        giftCards,
        customerCommissionDiscountCode,
        extras,
    }, bookingStart, bookingEnd, listing.attributes.publicData.pricing, paymentMode);
    const currency = lineItemCalculator.getCurrency();
    const protectedData = lineItemCalculator.getTransactionProtectedData();
    const stripePayinTotal = protectedData.payins.stripePayment;
    const requiresStripePayment = 0 < stripePayinTotal;
    const onExecuteOrder = (params) => __awaiter(void 0, void 0, void 0, function* () {
        var _y;
        const orderParams = getOrderParams(params);
        const transaction = (yield dispatch(initiateOrder(orderParams, instantBooking, (_y = pageState.transaction) === null || _y === void 0 ? void 0 : _y.id)));
        dispatch(setTransaction(transaction));
        return transaction;
    });
    const onPayNow = () => __awaiter(void 0, void 0, void 0, function* () {
        var _z, _0;
        handleCheckoutError(undefined);
        setPaymentInProgress(true);
        if (paymentMode === PaymentMode.Legacy &&
            !isApp &&
            (!pageState.giftCards ||
                (pageState.giftCards && pageState.giftCards.length === 0))) {
            return handleCheckoutError({
                code: 'legacy-payment-mode-without-gift-card',
                message: intl.formatMessage({
                    id: 'CheckoutPage.pleaseAddGiftCardOrUseCustomPaymentMode',
                }),
            });
        }
        if (giftCardInputNotEmpty) {
            return handleCheckoutError({
                code: 'code-not-redeemed',
                message: intl.formatMessage({
                    id: 'CheckoutPage.giftCardInputNotEmpty',
                }),
            });
        }
        if (requestPaymentTimestamp) {
            const transactionExpired = moment(requestPaymentTimestamp)
                .utc()
                .add(15, 'minutes')
                .isBefore(moment().utc());
            if (transactionExpired) {
                setShowExpiredTransactionModal(true);
                return undefined;
            }
        }
        if (requiresStripePayment) {
            return (_z = stripeFormRef === null || stripeFormRef === void 0 ? void 0 : stripeFormRef.current) === null || _z === void 0 ? void 0 : _z.requestSubmit();
        }
        const createdTransaction = yield onExecuteOrder({
            bookingStart,
            bookingEnd,
            listing,
            lineItemCalculator,
            giftCards,
            customerCommissionDiscountCode,
        });
        if (!((_0 = createdTransaction === null || createdTransaction === void 0 ? void 0 : createdTransaction.id) === null || _0 === void 0 ? void 0 : _0.uuid)) {
            return handleCheckoutError({
                message: intl.formatMessage({
                    id: 'CheckoutPage.stripeError.contactSupport',
                }),
                code: 'sharetribe-gift-card-only-initiate-order-failed',
            });
        }
        return onPaymentSuccessful(createdTransaction);
    });
    if (confirmPaymentInProgress) {
        return (_jsx(FullPageSheet, Object.assign({ open: true }, { children: _jsx(Layout, { children: _jsx(LayoutRow, { children: _jsxs(Information, { children: [_jsx(LoadingAnimation, {}), _jsx(Text, Object.assign({ variant: TextVariant.H30, as: "h1" }, { children: intl.formatMessage({
                                    id: 'CheckoutPage.confirmPaymentInProgress',
                                }) }))] }) }) }) })));
    }
    const inner = (_jsx(CheckoutPageContainer, Object.assign({ instantBookingAvailable: (_c = listing.attributes.publicData.instantBookingAvailable) !== null && _c !== void 0 ? _c : false, title: intl.formatMessage({ id: 'CheckoutPage.title' }, { listingTitle: listing.attributes.title }), noIndex: true }, { children: _jsx(LayoutRow, Object.assign({ horizontalVariant: LayoutRowHorizontalVariant.MobileUncontained }, { children: _jsxs(CheckoutLayout, { children: [_jsxs("div", { children: [_jsx(SpacedMobileView, Object.assign({ breakpoint: DefaultTheme.device.desktop }, { children: _jsx(ListingPreviewCard, { title: listing === null || listing === void 0 ? void 0 : listing.attributes.title, address: getLocationInformation((_d = listing === null || listing === void 0 ? void 0 : listing.attributes.publicData.location) === null || _d === void 0 ? void 0 : _d.address_components), hostName: (_e = listing.author) === null || _e === void 0 ? void 0 : _e.attributes.profile.displayName, imageSrc: (_g = (_f = listing.images) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.attributes.variants['scaled-small'].url, averageRating: (_j = (_h = listing.attributes) === null || _h === void 0 ? void 0 : _h.metadata) === null || _j === void 0 ? void 0 : _j.rating, campStyles: ((_l = (_k = listing.attributes) === null || _k === void 0 ? void 0 : _k.publicData) === null || _l === void 0 ? void 0 : _l.campStyle) || [] }) })), _jsx(BookingDetailsSection, { bookingStart: bookingStart, bookingEnd: bookingEnd, adultQuantity: adultQuantity, teenQuantity: teenQuantity, childQuantity: childQuantity, infantQuantity: infantQuantity, dogQuantity: dogQuantity, cancellationPolicyType: listing.attributes.publicData.cancellationPolicyType, checkinTime: getListingCheckInTime(listing) }), _jsx(PaymentSection, { requiresStripePayment: requiresStripePayment, stripePayinTotal: stripePayinTotal, lineItemCalculator: lineItemCalculator, formRef: stripeFormRef, onPaymentSuccessful: onPaymentSuccessful, currentUser: currentUser, bookingData: pageState.bookingData, listing: listing, providerStripeAccountId: providerStripeAccountId, existingTransaction: pageState.transaction, giftCards: giftCards, customerCommissionDiscountCode: customerCommissionDiscountCode, bookingStart: bookingStart, bookingEnd: bookingEnd, onCheckoutErrorOccurred: handleCheckoutError, onExecuteOrder: onExecuteOrder }), _jsx(MessageSection, { formRef: messageFormRef, transactionId: (_m = pageState.transaction) === null || _m === void 0 ? void 0 : _m.id, hostName: (_p = (_o = listing === null || listing === void 0 ? void 0 : listing.author) === null || _o === void 0 ? void 0 : _o.attributes) === null || _p === void 0 ? void 0 : _p.profile.displayName }), _jsx(ExpiredPaymentModal, { showModal: showExpiredTransactionModal, onClose: () => {
                                    setShowExpiredTransactionModal(false);
                                    setPaymentInProgress(false);
                                }, onAccept: () => {
                                    setShowExpiredTransactionModal(false);
                                    clearSessionStorageData(STORAGE_KEY);
                                    clearLocalStorageData(STORAGE_KEY_BOOKING_AUTH_FLOW);
                                    dispatch(resetListingPage());
                                    dispatch(resetCheckout());
                                    const listingUrl = getListingUrl(location.pathname);
                                    history.push(listingUrl);
                                } }), _jsx(MobileView, Object.assign({ breakpoint: DefaultTheme.device.desktop }, { children: _jsx(CheckoutSection, { listing: listing, bookingStart: bookingStart, bookingEnd: bookingEnd, seatQuantity: seatQuantity, adultQuantity: adultQuantity, teenQuantity: teenQuantity, childQuantity: childQuantity, infantQuantity: infantQuantity, dogQuantity: dogQuantity, extras: extras, giftCards: giftCards, customerCommissionDiscountCode: customerCommissionDiscountCode, onPayNow: onPayNow, paymentInProgress: paymentInProgress, error: checkoutError, "data-testid": "CheckoutSection_mobile" }) }))] }), _jsx(SidePanelContainer, Object.assign({ "data-testid": "CheckoutSection_desktop" }, { children: _jsxs(SidePanel, { children: [_jsx(ListingPreviewCard, { title: listing === null || listing === void 0 ? void 0 : listing.attributes.title, address: getLocationInformation((_q = listing === null || listing === void 0 ? void 0 : listing.attributes.publicData.location) === null || _q === void 0 ? void 0 : _q.address_components), hostName: (_r = listing.author) === null || _r === void 0 ? void 0 : _r.attributes.profile.displayName, imageSrc: (_t = (_s = listing.images) === null || _s === void 0 ? void 0 : _s[0]) === null || _t === void 0 ? void 0 : _t.attributes.variants['scaled-small'].url, averageRating: (_v = (_u = listing.attributes) === null || _u === void 0 ? void 0 : _u.metadata) === null || _v === void 0 ? void 0 : _v.rating, campStyles: ((_x = (_w = listing.attributes) === null || _w === void 0 ? void 0 : _w.publicData) === null || _x === void 0 ? void 0 : _x.campStyle) || [] }), _jsx(CheckoutInfo, { listing: listing, bookingStart: bookingStart, bookingEnd: bookingEnd, seatQuantity: seatQuantity, adultQuantity: adultQuantity, teenQuantity: teenQuantity, childQuantity: childQuantity, infantQuantity: infantQuantity, dogQuantity: dogQuantity, extras: extras, giftCards: giftCards, customerCommissionDiscountCode: customerCommissionDiscountCode, onPayNow: onPayNow, paymentInProgress: paymentInProgress, error: checkoutError })] }) }))] }) })) })));
    if (!requiresStripePayment) {
        return _jsx(Elements, Object.assign({ stripe: stripePromise }, { children: inner }));
    }
    return (_jsx(Elements, Object.assign({ stripe: stripePromise, options: {
            mode: 'payment',
            amount: stripePayinTotal,
            currency: currency.toLowerCase(),
            locale: currentUser && getDeeplLocale(locale),
            captureMethod: getCaptureMethod(paymentMode, instantBooking),
            paymentMethodTypes: getPaymentMethods(instantBooking, currency, paymentMode),
            appearance: {
                variables: {
                    fontFamily: 'Poppins, sans-serif',
                    colorText: DefaultTheme.color.cave,
                    colorDanger: DefaultTheme.color.rose,
                    colorSuccess: DefaultTheme.color.lagoon,
                    fontWeightNormal: '500',
                },
                rules: (checkoutError === null || checkoutError === void 0 ? void 0 : checkoutError.type) === 'card_error'
                    ? {
                        '.Input': {
                            border: `solid 1px ${DefaultTheme.color.rose}`,
                            color: DefaultTheme.color.rose,
                        },
                    }
                    : {},
            },
            fonts: [
                {
                    family: 'Poppins',
                    style: 'normal',
                    src: 'local("Poppins"), url(https://static.nomady.ch/fonts/poppins-v19-latin-300.woff)',
                },
            ],
        } }, { children: inner })));
};
export default CheckoutPage;
